<template>
  <div class="tabs-component-root w-full" :class="{ 'dialog-context': isDialog, 'fill-height-context': fillHeight }">
    <!-- Header Tabs -->
    <div v-if="!$vuetify.breakpoint.smAndDown"
      class="tabs-header-bar w-full border-b border-gray-200">
      <div class="tabs-header-pad w-full p-1 px-3">
        <div class="tabs-header-inner w-full min-w-0">
          <div class="tabs-scroll-area space-x-2 overflow-x-auto hide-scrollbar min-w-0 py-1">
            <button v-for="(tab, index) in tabs" :key="index" type="button" @click="setActiveTab(tab.key)"
              class="tab-pill-btn flex-shrink-0" :class="{
                'tab-pill-active': activeTab === tab.key,
                'tab-pill-inactive': activeTab !== tab.key
              }">
              <v-icon v-if="showIcons && tab.icon" left small class="tab-icon">
                {{ tab.icon }}
              </v-icon>
              <span>{{ tab.label }}</span>
            </button>
          </div>
          <!-- Filter Buttons for Each Tab -->
          <div class="tabs-actions-area">
            <slot name="tab-buttons" :activeTab="activeTab"></slot>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Header Tabs -->
    <ActitiveTabcomponent_mobile v-else :tabs="tabs" :active-tab="activeTab" :inside-dialog="isDialog"
      @tab-changed="setActiveTab">
      <template v-slot:tab-buttons="{ activeTab: mobileActiveTab }">
        <slot name="tab-buttons" :activeTab="mobileActiveTab"></slot>
      </template>
    </ActitiveTabcomponent_mobile>

    <!-- Dynamic Content Based on Active Tab -->
    <div class="tab-content" :style="dynamicHeightStyle">
      <div v-if="
        activeTabObject &&
        activeTabObject.permissionKey &&
        !hasIamPermission(activeTabObject.permissionKey)
      " class="pa-10 text-center grey--text">
        <v-icon size="64" color="grey lighten-2">mdi-lock-outline</v-icon>
        <div class="text-subtitle-1 font-weight-bold uppercase mt-4">Access Denied</div>
        <div class="text-caption">You do not have permission to view this tab's content.</div>
      </div>
      <slot v-else :name="activeTab" :activeTab="activeTab"></slot>
    </div>
  </div>
</template>

<script>
import { hasIamPermission } from '@/utils/iamEvaluator'
import ActitiveTabcomponent_mobile from './ActitiveTabcomponent_mobile.vue'

export default {
  name: 'ActiveTabComponent',
  components: {
    ActitiveTabcomponent_mobile
  },
  props: {
    tabs: {
      type: Array,
      required: true,
      validator: (tabs) => {
        return tabs.every(
          (tab) =>
            Object.prototype.hasOwnProperty.call(tab, 'key') &&
            Object.prototype.hasOwnProperty.call(tab, 'label') &&
            Object.prototype.hasOwnProperty.call(tab, 'component')
        )
      }
    },
    defaultTab: {
      type: String,
      default: null
    },
    insideDialog: {
      type: Boolean,
      default: false,
      description:
        'Set to true when component is used inside a dialog to prevent conflicting scrollbars'
    },
    showIcons: {
      type: Boolean,
      default: true,
      description: 'Whether to display icons in tabs'
    },
    fillHeight: {
      type: Boolean,
      default: false,
      description:
        'Set to true when the tab content manages its own scroll or fills the available height without outer scrolling'
    }
  },
  data() {
    return {
      activeTab: this.defaultTab || (this.tabs.length > 0 ? this.tabs[0].key : null),
      isInsideDialogDetected: false
    }
  },
  computed: {
    isDialog() {
      return this.insideDialog || this.isInsideDialogDetected
    },
    dynamicHeightStyle() {
      if (this.isDialog) {
        return {
          height: 'auto',
          maxHeight: 'none',
          overflow: 'visible'
        }
      }

      if (this.fillHeight) {
        return {
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden'
        }
      }

      return {
        maxHeight: 'calc(100vh - 160px)',
        overflowY: 'auto',
        overflowX: 'hidden'
      }
    },
    activeTabObject() {
      return this.tabs.find((t) => t.key === this.activeTab)
    },
    iamStatements() {
      return this.$store.state.wfmStatements || []
    }
  },
  mounted() {
    this.detectDialog()
  },
  updated() {
    this.detectDialog()
  },
  methods: {
    detectDialog() {
      if (!this.insideDialog && !this.isInsideDialogDetected && this.$el && this.$el.closest) {
        this.isInsideDialogDetected = Boolean(
          this.$el.closest('.v-dialog, .v-bottom-sheet, [role="dialog"], .v-overlay')
        )
      }
    },
    hasIamPermission(actionKey) {
      try {
        const userObj = JSON.parse(localStorage.getItem('user'))
        if (userObj) {
          const roles = userObj.role || userObj.cwRole || []
          if (roles.includes('WFM_ADMIN')) {
            return true
          }
        }
      } catch (e) {
        console.error('Error parsing user roles in active tab component:', e)
      }
      return hasIamPermission(this.iamStatements, actionKey)
    },
    setActiveTab(tabKey) {
      this.activeTab = tabKey
      this.$emit('tab-changed', tabKey)
    }
  },
  watch: {
    defaultTab(newVal) {
      if (newVal) {
        this.activeTab = newVal
      }
    },
    tabs: {
      handler(newTabs) {
        if (newTabs && newTabs.length > 0) {
          const exists = newTabs.some((t) => t.key === this.activeTab)
          if (!exists) {
            this.activeTab = this.defaultTab || newTabs[0].key
          }
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.tabs-component-root {
  width: 100% !important;
}

/* Page context: sticky below top app-bar */
.tabs-header-bar {
  width: 100% !important;
  background-color: #f3f7ff;
  position: sticky;
  top: 48px;
  z-index: 10;
}

/* Dialog context: sticky at top of modal (no 48px gap) */
.dialog-context .tabs-header-bar,
.v-dialog .tabs-header-bar {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* Fill-height context: static header in flex column */
.fill-height-context .tabs-header-bar {
  position: static !important;
  flex-shrink: 0 !important;
}

.tabs-header-pad {
  width: 100% !important;
}

.tabs-header-inner {
  width: 100% !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  min-width: 0 !important;
}

.tabs-scroll-area {
  display: flex !important;
  align-items: center !important;
  flex: 1 1 0% !important;
  min-width: 0 !important;
  overflow-x: auto !important;
  margin-right: 16px;
}

.tabs-actions-area {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  margin-left: auto !important;
  flex-shrink: 0 !important;
  height: 40px !important;
}

.tabs-actions-area >>> div {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.tab-pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  border-radius: 9999px;
  font-size: var(--font-subheading-sm);
  line-height: 1;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  border: none;
  outline: none;
  background-color: transparent;
  color: #454c59;
  user-select: none;
  box-sizing: border-box;
}

.tab-icon {
  font-size: 18px !important;
  color: inherit !important;
  transition: color 0.15s ease;
}

.tab-pill-btn:hover:not(.tab-pill-active) {
  color: #2563eb;
}

.tab-pill-btn.tab-pill-active {
  background-color: #ffffff !important;
  color: #2563eb !important;
  font-weight: 700 !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.08);
}

.dark .tabs-header-bar {
  background-color: #1f1f1f;
  border-color: #333333;
}

.dark .tab-pill-btn {
  color: #9ca3af;
}

.dark .tab-pill-btn:hover:not(.tab-pill-active) {
  color: #60a5fa;
}

.dark .tab-pill-btn.tab-pill-active {
  background-color: #2a2a2a !important;
  color: #60a5fa !important;
  font-weight: 700 !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.4);
}

.tab-content {
  transition: max-height 0.3s ease;
}

/* Dialog context: unconstrained natural height and visible overflow */
.dialog-context .tab-content,
.v-dialog .tab-content {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  transition: none;
}

/* Fill-height context: full flex column */
.tabs-component-root.fill-height-context {
  height: calc(100vh - 98px) !important;
  max-height: calc(100vh - 98px) !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.fill-height-context .tab-content {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.fill-height-context .tab-content > * {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

.v-btn {
  text-transform: none !important;
}

.buttonClass {
  font-size: var(--font-caption);
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
